/**
 * Created: 2026-10-10
 * Initial commit: Add admin appointments page (8f22a38a7)
 *
 * 课程预约 page: lists every coaching appointment of the current site in a grid,
 * split into the same three tabs as the mini coach appointments page (待确认,
 * 已确认, 已结束). Clicking a row opens the AppointmentDialog.
 * Updated: 2026-10-10 — Added a 学员 column (cancelled students left out);
 * the coach column no longer shows the mobile.
 * Updated: 2026-10-21 — Also used by the coach app: as a coach it queries
 * `role: COACH` with the site, which lists every appointment of the site.
 * Updated: 2026-10-21 — The dialog edits and cancels; the list reloads after.
 * Updated: 2026-10-22 — Titled 课程预约, as its menu item.
 * Updated: 2026-10-24 — Renamed from AppointmentsPage; routed at /sessions.
 */

import { gql } from '@apollo/client'
import { useLazyQuery } from '@apollo/client/react'
import { Alert, Box, Chip, Tab, Tabs, Typography } from '@mui/material'
import { DataGridPro, type GridColDef } from '@mui/x-data-grid-pro'
import { useCallback, useEffect, useRef, useState } from 'react'
import AppointmentDialog from '../components/AppointmentDialog'
import UserAvatar from '../components/UserAvatar'
import {
  AdminCoachingAppointmentsDocument,
  type AdminCoachingAppointmentsQuery,
  CoachingAppointmentRole,
  CoachingAppointmentTab,
  CoachingAttendeeStatus,
  CoachingSessionStatus,
  RosterMode,
} from '../gql/graphql'
import { useRole } from '../hooks'
import { getWeekdayText } from '../lib/dateUtils'

gql`
  query AdminCoachingAppointments(
    $role: CoachingAppointmentRole!
    $siteId: UUID!
    $tab: CoachingAppointmentTab!
    $first: Int
    $after: String
  ) {
    coachingAppointments(
      role: $role
      siteId: $siteId
      tab: $tab
      first: $first
      after: $after
    ) {
      edges {
        node {
          sessionId
          rosterMode
          status
          course {
            id
            name
          }
          coach {
            id
            openid
            name
            avatar
            gender
          }
          attendees {
            status
            student {
              id
              openid
              name
              avatar
              mobile
              gender
            }
          }
          slots {
            date
            hour
            court {
              id
              name
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`

type Connection = AdminCoachingAppointmentsQuery['coachingAppointments']
type AppointmentRow = Connection['edges'][number]['node']

const TABS: { value: CoachingAppointmentTab; label: string }[] = [
  { value: CoachingAppointmentTab.ToBeConfirmed, label: '待确认' },
  { value: CoachingAppointmentTab.Confirmed, label: '已确认' },
  { value: CoachingAppointmentTab.Completed, label: '已结束' },
]

const formatHour = (h: number) => `${String(h).padStart(2, '0')}:00`

/** "2026-10-05 周日 10:00-12:00 · 1号场", one line per date. */
function slotLines(slots: AppointmentRow['slots']): string[] {
  const byDate = new Map<string, AppointmentRow['slots']>()
  for (const s of slots) {
    byDate.set(s.date, [...(byDate.get(s.date) ?? []), s])
  }
  return [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, daySlots]) => {
      const hours = daySlots.map((s) => s.hour)
      const courts = [...new Set(daySlots.map((s) => s.court.name))].join('、')
      return `${date} ${getWeekdayText(date)} ${formatHour(
        Math.min(...hours),
      )}-${formatHour(Math.max(...hours) + 1)} · ${courts}`
    })
}

/** Mirrors the mini page: a confirmed appointment on the 已结束 tab is 已结束. */
function statusChip(
  row: AppointmentRow,
  completedTab: boolean,
): { label: string; color: 'success' | 'warning' | 'default' | 'info' } {
  switch (row.status) {
    case CoachingSessionStatus.Scheduled:
      return { label: '待确认', color: 'warning' }
    case CoachingSessionStatus.Cancelled:
      return { label: '已取消', color: 'default' }
    default:
      return completedTab
        ? { label: '已结束', color: 'info' }
        : { label: '已确认', color: 'success' }
  }
}

const emptyPageInfo: Connection['pageInfo'] = {
  __typename: 'PageInfo',
  hasNextPage: false,
  endCursor: null,
}

export default function SessionsPage() {
  const { currentSite, selectedRole } = useRole()
  const siteId = currentSite?.id
  const role =
    selectedRole.type === 'coach'
      ? CoachingAppointmentRole.Coach
      : CoachingAppointmentRole.Admin
  const [tab, setTab] = useState<CoachingAppointmentTab>(
    CoachingAppointmentTab.ToBeConfirmed,
  )
  const [edges, setEdges] = useState<Connection['edges']>([])
  const [pageInfo, setPageInfo] = useState<Connection['pageInfo']>(emptyPageInfo)
  const [dialogId, setDialogId] = useState<string | null>(null)

  const [execute, { loading, error, fetchMore }] = useLazyQuery(
    AdminCoachingAppointmentsDocument,
    { fetchPolicy: 'network-only' },
  )

  const executeRef = useRef(execute)
  executeRef.current = execute

  const loadingMore = useRef(false)
  const lastFetchedCursor = useRef<string | null | undefined>(undefined)

  // Load from the first page; also called after the dialog cancels or edits
  // an appointment.
  const loadFirstPage = useCallback(() => {
    if (!siteId) return
    loadingMore.current = false
    lastFetchedCursor.current = undefined
    setEdges([])
    setPageInfo(emptyPageInfo)
    executeRef.current({ variables: { role, siteId, tab } }).then((result) => {
      if (result.data) {
        setEdges(result.data.coachingAppointments.edges)
        setPageInfo(result.data.coachingAppointments.pageInfo)
      }
    })
  }, [role, siteId, tab])

  // Reload from the first page whenever the role, site or tab changes.
  useEffect(() => {
    loadFirstPage()
  }, [loadFirstPage])

  const handleRowsScrollEnd = useCallback(() => {
    if (
      pageInfo.hasNextPage &&
      !loadingMore.current &&
      pageInfo.endCursor !== lastFetchedCursor.current
    ) {
      loadingMore.current = true
      lastFetchedCursor.current = pageInfo.endCursor
      fetchMore({ variables: { after: pageInfo.endCursor } }).then((result) => {
        const more = result.data
        if (more) {
          setEdges((prev) => {
            const existingIds = new Set(prev.map((e) => e.node.sessionId))
            const newEdges = more.coachingAppointments.edges.filter(
              (e) => !existingIds.has(e.node.sessionId),
            )
            return [...prev, ...newEdges]
          })
          setPageInfo(more.coachingAppointments.pageInfo)
        }
        loadingMore.current = false
      })
    }
  }, [pageInfo, fetchMore])

  const completedTab = tab === CoachingAppointmentTab.Completed

  const columns: GridColDef<AppointmentRow>[] = [
    {
      field: '__rowNumber',
      headerName: '#',
      width: 50,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) =>
        params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
    },
    {
      field: 'course',
      headerName: '课程',
      width: 180,
      sortable: false,
      headerAlign: 'center',
      valueGetter: (_value, row) => row.course.name,
    },
    {
      field: 'coach',
      headerName: '教练',
      width: 180,
      sortable: false,
      headerAlign: 'center',
      renderCell: ({ row }) => <UserAvatar user={row.coach} showMobile={false} />,
    },
    {
      field: 'attendees',
      headerName: '学员',
      width: 200,
      sortable: false,
      headerAlign: 'center',
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {row.attendees
            .filter((a) => a.status !== CoachingAttendeeStatus.Cancelled)
            .map((a) => (
              <UserAvatar key={a.student.openid} user={a.student} />
            ))}
        </Box>
      ),
    },
    {
      field: 'slots',
      headerName: '上课时间',
      flex: 1,
      minWidth: 280,
      sortable: false,
      headerAlign: 'center',
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {slotLines(row.slots).map((line) => (
            <Typography key={line} variant="body2">
              {line}
            </Typography>
          ))}
        </Box>
      ),
    },
    {
      field: 'rosterMode',
      headerName: '方式',
      width: 90,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (_value, row) =>
        row.rosterMode === RosterMode.Invite ? '邀请' : '报名',
    },
    {
      field: 'status',
      headerName: '状态',
      width: 100,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => {
        const st = statusChip(row, completedTab)
        return <Chip label={st.label} color={st.color} size="small" />
      },
    },
  ]

  const rows = edges.map((e) => e.node)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography variant="h4">课程预约</Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(_e, v: CoachingAppointmentTab) => setTab(v)}
        sx={{ mb: 1 }}
      >
        {TABS.map((t) => (
          <Tab key={t.value} value={t.value} label={t.label} />
        ))}
      </Tabs>

      {error && <Alert severity="error">加载约课失败: {error.message}</Alert>}

      <DataGridPro
        rows={rows}
        columns={columns}
        getRowId={(row: AppointmentRow) => row.sessionId}
        loading={loading}
        hideFooter
        disableRowSelectionOnClick
        disableColumnMenu
        density="compact"
        getRowHeight={() => 'auto'}
        onRowsScrollEnd={handleRowsScrollEnd}
        onRowClick={({ row }: { row: AppointmentRow }) => setDialogId(row.sessionId)}
        localeText={{ noRowsLabel: '暂无约课' }}
        sx={{
          flex: 1,
          minHeight: 0,
          '& .MuiDataGrid-row': { cursor: 'pointer' },
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
            py: 1,
          },
        }}
      />

      <AppointmentDialog
        sessionId={dialogId}
        onClose={() => setDialogId(null)}
        onChanged={loadFirstPage}
      />
    </Box>
  )
}
